import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import microbiologyService from '../../services/microbiologyService';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './MicrobiologyWorkbench.css';

export default function MicrobiologyWorklistPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [specimens, setSpecimens] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSpecimen, setSelectedSpecimen] = useState('');
  const [onlyCritical, setOnlyCritical] = useState(false);

  // Centralized Server-Side Pagination
  const pagination = usePagination({
    totalCount,
    initialPage: 1,
    initialPageSize: 25,
    resetDeps: [searchTerm, selectedStatus, selectedSpecimen, onlyCritical]
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [wlRes, spRes] = await Promise.all([
        microbiologyService.getWorklist({
          searchTerm: searchTerm || undefined,
          status: selectedStatus || undefined,
          specimenID: selectedSpecimen || undefined,
          onlyCritical: onlyCritical,
          pageIndex: pagination.page,
          pageSize: pagination.pageSize
        }),
        microbiologyService.getSpecimens()
      ]);

      const data = wlRes?.data || {};
      setItems(data.items || []);
      setTotalCount(data.totalCount || 0);
      setSpecimens(spRes?.data || []);
    } catch (err) {
      console.error('Error loading microbiology worklist:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedStatus, selectedSpecimen, onlyCritical, pagination.page, pagination.pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Metrics count calculations
  const pendingCount = items.filter((i) => i.cultureStatus === 'Pending' || !i.cultureStatus).length;
  const growthCount = items.filter((i) => i.cultureStatus === 'Growth').length;
  const criticalCount = items.filter((i) => i.isCriticalAlert).length;
  const approvedCount = items.filter((i) => i.approved).length;

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Microbiology Culture & AST Workbench Queue
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            Multi-center microbiology order queue, incubation monitoring, and pathogen validation
          </p>
        </div>
        <div>
          <button
            className="micro-btn-secondary"
            onClick={() => navigate('/masters/microbiology')}
          >
            ⚙️ Microbiology Configuration Hub
          </button>
        </div>
      </div>

      {/* Metric Stat Cards */}
      <div className="worklist-metrics-grid">
        <div className="metric-card" style={{ borderLeft: '4px solid #0284c7' }}>
          <div className="metric-card-val">{totalCount}</div>
          <div className="metric-card-label">Total Active Cases</div>
        </div>

        <div className="metric-card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="metric-card-val" style={{ color: '#d97706' }}>{pendingCount}</div>
          <div className="metric-card-label">Incubation In-Progress</div>
        </div>

        <div className="metric-card" style={{ borderLeft: '4px solid #10b981' }}>
          <div className="metric-card-val" style={{ color: '#059669' }}>{growthCount}</div>
          <div className="metric-card-label">Positive Growth (AST Queue)</div>
        </div>

        <div className="metric-card" style={{ borderLeft: '4px solid #ef4444' }}>
          <div className="metric-card-val" style={{ color: '#dc2626' }}>{criticalCount}</div>
          <div className="metric-card-label">Critical Alerts (Infection Control)</div>
        </div>

        <div className="metric-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <div className="metric-card-val" style={{ color: '#7c3aed' }}>{approvedCount}</div>
          <div className="metric-card-label">Signed & Released</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="micro-filter-bar">
        <div className="micro-search-input">
          <input
            type="text"
            placeholder="Search by UHID, Patient Name, Lab No, or Barcode SIN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="micro-filter-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">All Culture Statuses</option>
          <option value="Pending">Pending Incubation</option>
          <option value="In Progress">In Progress</option>
          <option value="Growth">Significant Growth</option>
          <option value="No Growth">No Growth (Negative)</option>
          <option value="Mixed Growth">Mixed Growth / Contaminated</option>
        </select>

        <select
          className="micro-filter-select"
          value={selectedSpecimen}
          onChange={(e) => setSelectedSpecimen(e.target.value)}
        >
          <option value="">All Specimens</option>
          {specimens.map((sp) => (
            <option key={sp.specimenID} value={sp.specimenID}>
              {sp.specimenName}
            </option>
          ))}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: '600', color: '#dc2626', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={onlyCritical}
            onChange={(e) => setOnlyCritical(e.target.checked)}
          />
          Critical Alerts Only
        </label>
      </div>

      {/* Worklist Table */}
      <div className="micro-table-wrapper">
        <table className="micro-table">
          <thead>
            <tr>
              <th>Lab No</th>
              <th>UHID</th>
              <th>Patient Name</th>
              <th>Age / Sex</th>
              <th>Specimen</th>
              <th>Investigation</th>
              <th>Culture Status</th>
              <th>Incubation</th>
              <th>Organisms</th>
              <th>Alerts</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '36px' }}>
                  Loading microbiology queue...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '36px' }}>
                  No microbiology culture orders found matching your filters.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.testID}>
                  <td>
                    <strong>{item.ledgerTransactionNo}</strong>
                    {item.barcodeNo && (
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        SIN: {item.barcodeNo}
                      </div>
                    )}
                  </td>
                  <td>
                    <code>{item.patientID}</code>
                  </td>
                  <td>
                    <strong>{item.patientName}</strong>
                  </td>
                  <td>{item.ageDisplay} &bull; {item.gender}</td>
                  <td>
                    <span className="micro-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                      {item.specimenName}
                    </span>
                  </td>
                  <td>{item.itemName}</td>
                  <td>
                    <span
                      className="micro-badge"
                      style={{
                        background:
                          item.cultureStatus === 'Growth'
                            ? '#fee2e2'
                            : item.cultureStatus === 'No Growth'
                            ? '#dcfce7'
                            : '#fef3c7',
                        color:
                          item.cultureStatus === 'Growth'
                            ? '#b91c1c'
                            : item.cultureStatus === 'No Growth'
                            ? '#15803d'
                            : '#b45309'
                      }}
                    >
                      {item.cultureStatus || 'Pending'}
                    </span>
                  </td>
                  <td>
                    {item.daysInIncubation > 0 ? (
                      <div>
                        <span style={{ fontSize: '0.8rem', color: '#334155', fontWeight: '600' }}>
                          Day {item.daysInIncubation}
                        </span>
                        {item.daysInIncubation >= 2 && !item.approved && (
                          <div style={{ fontSize: '0.68rem', color: '#b91c1c', fontWeight: '700', marginTop: '2px' }}>
                            ⏱️ TAT Alert
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Day 0 (Fresh)</span>
                    )}
                  </td>
                  <td>
                    {item.organismCount > 0 ? (
                      <span className="micro-badge" style={{ background: '#ede9fe', color: '#6d28d9' }}>
                        {item.organismCount} isolate(s)
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>None</span>
                    )}
                  </td>
                  <td>
                    {item.isCriticalAlert ? (
                      <span className="micro-badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                        🚨 Critical Alert
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <button
                      className="micro-btn-primary"
                      style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                      onClick={() => navigate(`/microbiology/workbench/${item.testID}`)}
                    >
                      Open Workbench &rarr;
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Centralized Standard LIMS Pagination */}
      <CommonPagination
        {...pagination.paginationProps}
        loading={loading}
      />
    </div>
  );
}
